import { useMemo } from 'react'

import { ActionFlag, IActionRepeaterValue, IActionType } from '@makyoapp/makyo-common'
import { BaseDialog, Button, Dialog, Image, RichText } from '@makyoapp/makyo-tw-ui'

import { cn } from '@/lib/cn'
import { isDescendant, parseStringToDescendant } from '@/lib/slate'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { hasActionFlag } from '@/shared/builder-utils'
import { isEmpty } from '@/shared/utils'
import { CompVariableText } from '@/components/__project__/shared/CompVariableText'
import { convertRatioFormat } from '@/components/__project__/utils'

const DEFAULT_CLOSE_ACTION = [
  {
    type: IActionType.CloseAlert,
    settings: {},
  },
] as IActionRepeaterValue[]

export function BuilderDialog() {
  // access builder context
  const { dialogs, dialogAction } = useAppBuilderContext()
  const { device } = useBreakpoint()

  // access builder action
  const { simulateAction } = useBuilderActionContextV2()

  const currentDialog = useMemo(() => {
    return dialogs.find(({ id }) => id === dialogAction?.dialog.id)
  }, [dialogs, dialogAction?.dialog.id])

  const dialogName = useVariableTextSingleCell(currentDialog?.name)
  const dialogHeadline = useVariableTextSingleCell(currentDialog?.headline)
  const dialogContent = currentDialog?.content // useVariableTextSingleCell(currentDialog?.content)
  const dialogLeadingLabel = useVariableTextSingleCell(currentDialog?.leadingLabel)
  const dialogTrailingLabel = useVariableTextSingleCell(currentDialog?.trailingLabel)

  const iconAlignment = currentDialog?.alignment?.iconAlignment
  const titleAlignment = currentDialog?.alignment?.titleAlignment
  const contentAlignment = currentDialog?.alignment?.contentAlignment
  const footerAlignment = currentDialog?.alignment?.footerAlignment
  const showDivider = currentDialog?.showDivider
  const showIcon = currentDialog?.showIcon
  const width = currentDialog?.width

  const parsedDialogContent = useMemo(() => {
    if (!dialogContent) return null

    if (isDescendant(dialogContent)) {
      const descendant = parseStringToDescendant(dialogContent)
      return (
        <CompVariableText
          nodes={descendant}
          containerClient={null as any}
          textProps={{
            style: {
              whiteSpace: 'pre-wrap',
              textAlign: contentAlignment === 'center' ? 'center' : contentAlignment === 'left' ? 'left' : 'right',
            },
          }}
          className='mb-3 last:mb-0'
        />
      )
    }

    return (
      <RichText
        text={dialogContent}
        backgroundColor='neutral-100'
        textAlignment={contentAlignment as any}
        style={{
          font: 'var(--md-sys-typescale-body-medium)',
          textAlign: contentAlignment === 'center' ? 'center' : contentAlignment === 'left' ? 'left' : 'right',
        }}
      />
    )
  }, [dialogContent, contentAlignment])

  return dialogAction && hasActionFlag(dialogAction.flag, ActionFlag.Preview) ? (
    <Dialog
      onOpenChange={(open) => {
        if (!open) {
          simulateAction({
            actions: DEFAULT_CLOSE_ACTION,
            dialogId: currentDialog?.id,
          })
        }
      }}
      contentClassName={cn('max-w-full p-4', width ? `w-[${width}%]` : 'w-[400px]')}
      open
      // modal={false}
      // overlayOpacity={0.7}
      titleTypography={{ typography: (currentDialog?.headlineTypography as any) ?? 'title-large' }}
      title={dialogHeadline}
      backgroundColor='neutral-100'
      iconName={dialogAction.dialog.icon ? dialogAction.dialog.icon : undefined}
      iconSize={32}
      cornerRadius='extra-large'
      iconAlignment={iconAlignment as any}
      titleAlignment={titleAlignment as any}
      contentAlignment={contentAlignment as any}
      contentTypography={{ typography: (currentDialog?.contentTypography as any) ?? 'body-medium' }}
      footerAlignment={footerAlignment as any}
      showFooterDivider={showDivider as any}
      showIcon={showIcon as any}
      width={width ? width[device] : undefined}
      confirmButtonColorVariant={currentDialog?.leadingButtonColor as any}
      cancelButtonColorVariant={currentDialog?.trailingButtonColor as any}
      footer={
        <div className='flex items-center gap-2'>
          {currentDialog?.showLeading && (
            <Button
              padding={{ top: '8px', bottom: '8px', left: '16px', right: '16px' }}
              colorVariant={currentDialog?.leadingButtonColor ?? ('neutral-100' as any)}
              onClick={() => {
                // close dialog action will be moved here, if specified
                simulateAction({
                  actions: isEmpty(dialogAction.dialog.leadingActions)
                    ? DEFAULT_CLOSE_ACTION
                    : dialogAction.dialog.leadingActions,
                  dialogId: currentDialog?.id,
                })
              }}
            >
              {dialogLeadingLabel ?? 'Cancel'}
            </Button>
          )}

          {currentDialog?.showTrailing && (
            <Button
              padding={{ top: '8px', bottom: '8px', left: '16px', right: '16px' }}
              colorVariant={currentDialog?.trailingButtonColor ?? ('primary-50' as any)}
              onClick={() => {
                simulateAction({
                  actions: isEmpty(dialogAction.dialog.trailingActions)
                    ? DEFAULT_CLOSE_ACTION
                    : dialogAction.dialog.trailingActions,
                  dialogId: currentDialog?.id,
                })
              }}
            >
              {dialogTrailingLabel ?? 'Ok'}
            </Button>
          )}
        </div>
      }
    >
      <div
        className={cn(
          'text-lg text-auto',
          contentAlignment === 'center' ? 'text-center' : contentAlignment === 'left' ? 'text-left' : 'text-right'
        )}
        style={{
          font: 'var(--md-sys-typescale-body-medium)',
        }}
      >
        {parsedDialogContent}
      </div>
    </Dialog>
  ) : null
}

export function BuilderGalleryDialog() {
  const { galleryModal, setGalleryModal } = useAppBuilderContext()
  const [colorScheme] = useBuilderColorSchemeV2()

  if (!galleryModal) return null

  return (
    <BaseDialog
      open={!!galleryModal}
      onOpenChange={(open) => {
        setGalleryModal(null)
      }}
    >
      <BaseDialog.Content
        className={cn('p-6', 'w-[90%] max-w-4xl', 'mx-auto')}
        style={{
          width: galleryModal.width,
        }}
        cornerRadius='medium'
        backgroundColor='primary-95'
        backdropColor={colorScheme.current === 'dark' ? 'neutral-100' : 'neutral-0'}
        backdropOpacity={0.6}
      >
        <Image className='w-full' src={galleryModal.src} ratio={convertRatioFormat(galleryModal.aspectRatio) as any} />
      </BaseDialog.Content>
    </BaseDialog>
  )
}
